import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollText, Search, Loader2, LogIn, LogOut, Lock, KeyRound, UserPlus, UserCog, Trash2, Users as UsersIcon, FolderPlus, FolderCog, FolderMinus } from "lucide-react";

const ACTIONS = [
  "login_success", "login_failed", "login_lockout",
  "password_change", "password_reset",
  "student_create", "student_update", "student_delete",
  "group_create", "group_update", "group_delete",
  "admin_create", "admin_update", "admin_delete",
];

const META = {
  login_success: { icon: LogIn, tint: "#10B981" },
  login_failed: { icon: LogOut, tint: "#F59E0B" },
  login_lockout: { icon: Lock, tint: "#EF4444" },
  password_change: { icon: KeyRound, tint: "#6366F1" },
  password_reset: { icon: KeyRound, tint: "#8B5CF6" },
  student_create: { icon: UserPlus, tint: "#10B981" },
  student_update: { icon: UserCog, tint: "#3B82F6" },
  student_delete: { icon: Trash2, tint: "#EF4444" },
  group_create: { icon: FolderPlus, tint: "#10B981" },
  group_update: { icon: FolderCog, tint: "#3B82F6" },
  group_delete: { icon: FolderMinus, tint: "#EF4444" },
  admin_create: { icon: UserPlus, tint: "#10B981" },
  admin_update: { icon: UserCog, tint: "#3B82F6" },
  admin_delete: { icon: Trash2, tint: "#EF4444" },
};

const PAGE = 25;

export default function AuditLog() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [action, setAction] = useState("all");
  const [search, setSearch] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (reset) => {
    setLoading(true);
    try {
      const s = reset ? 0 : skip;
      const r = await api.get("/audit-logs", { params: { action, search: search.trim(), limit: PAGE, skip: s } });
      setTotal(r.data.total);
      setItems(reset ? r.data.items : (prev) => [...prev, ...r.data.items]);
      setSkip(s + PAGE);
    } finally {
      setLoading(false);
    }
  }, [action, search, skip]);

  useEffect(() => {
    const handler = setTimeout(() => load(true), 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, search]);

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground flex items-center gap-2">
          <ScrollText className="h-6 w-6" /> {t("audit.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("audit.subtitle")}</p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">{t("audit.action")}</label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-[220px]" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("audit.allActions")}</SelectItem>
              {ACTIONS.map((a) => <SelectItem key={a} value={a}>{t(`audit.actions.${a}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 flex-1 min-w-[220px]">
          <label className="text-xs font-medium text-muted-foreground">{t("audit.search")}</label>
          <div className="relative">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("audit.searchPh")} data-testid="audit-search" className="ps-9" />
          </div>
        </div>
        <div className="text-sm text-muted-foreground pb-2" data-testid="audit-total">{t("audit.total")}: {total}</div>
      </div>

      <div className="surface-card overflow-hidden rounded-xl border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="border-b border-border bg-secondary/60">
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("audit.time")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("audit.event")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("audit.actor")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("audit.target")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("audit.detail")}</th>
                <th className="px-4 py-3 text-start font-medium text-muted-foreground">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">{t("audit.empty")}</td></tr>
              )}
              {items.map((it) => {
                const m = META[it.action] || { icon: ScrollText, tint: "#6B7280" };
                const Icon = m.icon;
                return (
                  <tr key={it.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`audit-row-${it.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground" dir="ltr">{fmt(it.timestamp)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium" style={{ backgroundColor: `${m.tint}1a`, color: m.tint }}>
                        <Icon className="h-3.5 w-3.5" /> {t(`audit.actions.${it.action}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{it.actor_name || "—"}</div>
                      {it.actor_phone && <div className="text-xs text-muted-foreground" dir="ltr">{it.actor_phone}</div>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{it.target_name || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{it.detail || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground" dir="ltr">{it.ip || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {items.length < total && (
        <div className="flex justify-center">
          <button onClick={() => load(false)} disabled={loading} data-testid="audit-load-more"
            className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("audit.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}
